import './card-list.css';
import { Card, Popconfirm, Spin } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Fragment, useEffect, useRef, useState } from 'react';

// Компактный список карточек (одна в строку).
// props: items, renderTitle(item) — первая строка, renderBody(item) — вторая строка (элементы в flex),
//        onClick(item, event), onDelete(item), deleteTitle, emptyText, rowStyle(item), loading,
//        canDelete(item) — показывать иконку удаления (по умолчанию true),
//        pageSize — если задан, карточки подгружаются порциями при прокрутке (клиентская подгрузка),
//        cardProps(item) — дополнительные props карточки (className, draggable, onDragStart …),
//        renderAside(item) — колонка слева на всю высоту карточки (например логотип),
//        groupOf(item) — подпись группы; заголовок ставится, когда подпись меняется
const CardList = ({
    items, renderTitle, renderBody, onClick, onDelete, deleteTitle = 'Удалить?', emptyText = 'Нет данных',
    rowStyle, loading = false, pageSize, cardProps, canDelete, renderAside, groupOf,
}) => {
    const [visible, setVisible] = useState(pageSize ?? Infinity);
    const sentinelRef = useRef(null);
    const total = items?.length ?? 0;

    useEffect(() => { setVisible(pageSize ?? Infinity); }, [items, pageSize]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !pageSize) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) setVisible((v) => Math.min(v + pageSize, total));
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [total, pageSize]);

    if (loading && total === 0) {
        return <div className="card-list-empty"><Spin size="small" /></div>;
    }
    if (total === 0) {
        return <div className="card-list-empty">{emptyText}</div>;
    }
    const shown = pageSize ? items.slice(0, visible) : items;
    let previousGroup;
    return (
        <div className="card-list">
            {shown.map((item) => {
                const group = groupOf?.(item);
                const showGroup = group && group !== previousGroup;
                previousGroup = group;
                const { className, ...extra } = cardProps?.(item) ?? {};
                const aside = renderAside?.(item);
                const main = (
                    <>
                        <div className="card-list-title">
                            {renderTitle(item)}
                            {onDelete && (canDelete?.(item) ?? true) && (
                                <span className="card-list-actions" onClick={(e) => e.stopPropagation()}>
                                    <Popconfirm title={deleteTitle} onConfirm={() => onDelete(item)} okText="Да" cancelText="Нет">
                                        <DeleteOutlined className="row-delete-icon" />
                                    </Popconfirm>
                                </span>
                            )}
                        </div>
                        {renderBody && <div className="card-list-body">{renderBody(item)}</div>}
                    </>
                );
                return (
                    <Fragment key={item.id}>
                    {showGroup && <div className="card-list-group">{group}</div>}
                    <Card
                        size="small"
                        hoverable={!!onClick}
                        className={`card-list-item${className ? ` ${className}` : ''}`}
                        style={rowStyle?.(item)}
                        onClick={onClick ? (e) => onClick(item, e) : undefined}
                        {...extra}
                    >
                        {aside
                            ? (
                                <div className="card-list-with-aside">
                                    {aside}
                                    <div className="card-list-main">{main}</div>
                                </div>
                            )
                            : main}
                    </Card>
                    </Fragment>
                );
            })}
            {pageSize && (
                <div ref={sentinelRef} className="card-list-sentinel">
                    {visible < total ? 'Загрузка…' : `Всего: ${total}`}
                </div>
            )}
        </div>
    );
};

// Строка «иконка + текст» для тела карточки
export const CardRow = ({ icon, children, title, className }) => {
    if (children == null || children === '') return null;
    return (
        <span className={`card-list-row ${className ?? ''}`} title={title ?? (typeof children === 'string' ? children : undefined)}>
            {icon}
            <span className="card-list-ellipsis">{children}</span>
        </span>
    );
};

export default CardList;
